import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { closeIconTask } from '../../static/svgs';
import {
  getBusinessCompanies,
  getEmployeeAccessibleCompanies,
  setBusinessCompanyInWork,
  setEmployeeCompanyInWork,
} from '../login/loginApi';
import '../tasks/task.css';
import '../shared/companyPicker.css';

/**
 * Выбор компаний из каталога профиля для добавления на Главную (in_work=1).
 */
function AddWorkspaceCompanyModal({ businessUserId, onClose, onAdded }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  const loadCandidates = useCallback(async () => {
    if (!businessUserId) {
      setCandidates([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [ownedResult, delegatedResult] = await Promise.all([
        getBusinessCompanies(businessUserId, { inWork: 0 }),
        getEmployeeAccessibleCompanies(businessUserId, 'all', { inWork: 0 }),
      ]);
      const byId = new Map();
      if (ownedResult.success) {
        (ownedResult.companies || []).forEach((c) => {
          if (!c.company_id) return;
          byId.set(Number(c.company_id), {
            company_id: Number(c.company_id),
            name: c.name || `Компания #${c.company_id}`,
            inn: c.inn || null,
            access_mode: 'owner',
          });
        });
      }
      if (delegatedResult.success) {
        (delegatedResult.companies || []).forEach((c) => {
          if (!c.company_id) return;
          const id = Number(c.company_id);
          if (byId.has(id)) return;
          byId.set(id, {
            company_id: id,
            name: c.name || `Компания #${id}`,
            inn: c.inn || null,
            access_mode: 'delegated',
          });
        });
      }
      setCandidates(
        [...byId.values()].sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''), 'ru')
        )
      );
      if (!ownedResult.success && !delegatedResult.success) {
        setError(ownedResult.error || delegatedResult.error || 'Не удалось загрузить список');
      }
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить список');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [businessUserId]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleAdd = async (company) => {
    if (!company?.company_id || savingId) return;
    setSavingId(company.company_id);
    setError('');
    try {
      const result =
        company.access_mode === 'delegated'
          ? await setEmployeeCompanyInWork(businessUserId, company.company_id, true)
          : await setBusinessCompanyInWork(businessUserId, company.company_id, true);
      if (!result.success) {
        setError(result.error || 'Не удалось добавить');
        return;
      }
      await onAdded?.(company);
      onClose?.();
    } catch (e) {
      console.error(e);
      setError('Ошибка добавления компании');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="modal-container">
      <div className="modal-content">
        <div className="close-button" onClick={onClose}>
          {closeIconTask}
        </div>
        <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
          Добавить в работу
        </div>
        <div className="manrope-bold" style={{ marginTop: 12, fontSize: 14, color: '#64748b', textAlign: 'center' }}>
          Компании из раздела Профиль → Компании
        </div>
        <div style={{ width: '100%', marginTop: 20 }}>
          {loading ? (
            <div className="manrope-bold" style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
              Загрузка…
            </div>
          ) : candidates.length === 0 ? (
            <div className="manrope-bold" style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>
              Нет компаний вне работы. Добавьте компанию по ИНН в Профиле → Компании.
            </div>
          ) : (
            <div className="company-picker-list company-picker-panel">
              {candidates.map((company) => (
                <div key={`${company.access_mode}-${company.company_id}`} className="company-picker-item">
                  <button
                    type="button"
                    className="company-picker-main-btn manrope-bold"
                    onClick={() => handleAdd(company)}
                    disabled={!!savingId}
                  >
                    <div className="company-picker-main-top">
                      <span className="company-picker-name">{company.name}</span>
                    </div>
                    {company.inn ? (
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, textAlign: 'left' }}>
                        ИНН: {company.inn}
                        {company.access_mode === 'delegated' ? ' · доступ' : ''}
                      </div>
                    ) : null}
                    {savingId === company.company_id ? (
                      <div style={{ fontSize: 13, color: '#3262ff', marginTop: 4 }}>Добавление…</div>
                    ) : null}
                  </button>
                </div>
              ))}
            </div>
          )}
          {error ? (
            <div className="manrope-bold" style={{ color: '#ef4444', marginTop: 12, textAlign: 'center' }}>
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default AddWorkspaceCompanyModal;
